// frontend/src/pages/Agenda.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../api';

// ─── Paleta Sobraa ────────────────────────────────────────────────────────────
const C = {
  navy:      '#1A2233',
  navyD:     '#111827',
  navyL:     '#243044',
  orange:    '#D85A30',
  orangeH:   '#B84A24',
  orangeL:   'rgba(216,90,48,0.12)',
  bg:        '#f5f7fa',
  surface:   '#ffffff',
  border:    '#d4dde8',
  muted:     '#6b7a8d',
  text:      '#1A2233',
  verde:     '#27ae60',
  verdeL:    'rgba(39,174,96,0.12)',
  vermelho:  '#e74c3c',
  vermelhoL: 'rgba(231,76,60,0.12)',
  amarelo:   '#f59e0b',
  amareloL:  'rgba(245,158,11,0.12)',
  azul:      '#2563eb',
  azulL:     'rgba(37,99,235,0.12)',
  roxo:      '#7c3aed',
  roxoL:     'rgba(124,58,237,0.12)',
};

// ─── Cores disponíveis para eventos ──────────────────────────────────────────
const CORES_EVENTO = [
  { hex: '#D85A30', nome: 'Laranja' },
  { hex: '#2563eb', nome: 'Azul' },
  { hex: '#27ae60', nome: 'Verde' },
  { hex: '#7c3aed', nome: 'Roxo' },
  { hex: '#e74c3c', nome: 'Vermelho' },
  { hex: '#f59e0b', nome: 'Amarelo' },
  { hex: '#0891b2', nome: 'Ciano' },
  { hex: '#1A2233', nome: 'Navy' },
];

// Paleta de cores para técnicos (gerada automaticamente por ID)
const CORES_TECNICOS = [
  '#2563eb', '#27ae60', '#7c3aed', '#e74c3c',
  '#f59e0b', '#0891b2', '#D85A30', '#059669',
  '#dc2626', '#7c2d12', '#1d4ed8', '#15803d',
];

// Mapa global de cores dos técnicos — atualizado pelo componente Agenda
let _mapaCorTecnicos = {};

function corDoEvento(ev, _ignorado) {
  // Usa sempre o mapa global (ignorar segundo parâmetro legado)
  if (ev.tecnico_id && _mapaCorTecnicos[ev.tecnico_id]) {
    return _mapaCorTecnicos[ev.tecnico_id];
  }
  if (ev.tecnico_id) {
    const idx = Number(ev.tecnico_id) % CORES_TECNICOS.length;
    return CORES_TECNICOS[idx];
  }
  return '#6b7a8d';
}

// ─── Status ───────────────────────────────────────────────────────────────────
const STATUS_LIST = [
  { val: 'agendado',    label: 'Agendado',    cor: C.azul },
  { val: 'em_andamento',label: 'Em andamento',cor: C.amarelo },
  { val: 'concluido',   label: 'Concluído',   cor: C.verde },
  { val: 'cancelado',   label: 'Cancelado',   cor: C.vermelho },
];

const MESES = ['Janeiro','Fevereiro','Março','Abril','Maio','Junho',
               'Julho','Agosto','Setembro','Outubro','Novembro','Dezembro'];
const DIAS_SEMANA_FULL = ['Domingo','Segunda','Terça','Quarta','Quinta','Sexta','Sábado'];
const DIAS_SEMANA_SHORT = ['Dom','Seg','Ter','Qua','Qui','Sex','Sáb'];

// ─── Helpers ──────────────────────────────────────────────────────────────────
function fmtData(dtStr) {
  if (!dtStr) return '';
  // Usa substring para evitar conversão de fuso (UTC -> local)
  const parts = dtStr.substring(0, 10).split('-');
  if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
  return dtStr.substring(0, 10);
}
function fmtHora(dtStr) {
  if (!dtStr) return '';
  const d = new Date(dtStr);
  return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
}
function fmtDatetimeLocal(dtStr) {
  if (!dtStr) return '';
  const d = new Date(dtStr);
  const pad = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
function fmtDateInput(dtStr) {
  // Usa substring para evitar conversão de fuso que causa dia anterior
  if (!dtStr) return '';
  return dtStr.substring(0, 10);
}
function startOfWeek(date) {
  const d = new Date(date);
  d.setDate(d.getDate() - d.getDay());
  d.setHours(0,0,0,0);
  return d;
}
function isSameDay(a, b) {
  return a.getFullYear() === b.getFullYear() &&
         a.getMonth() === b.getMonth() &&
         a.getDate() === b.getDate();
}
function parseDateLocal(dtStr) {
  if (!dtStr) return null;
  const s = dtStr.substring(0, 10); // "YYYY-MM-DD"
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d, 0, 0, 0, 0);
}

function eventOnDay(ev, day) {
  const dayStart = new Date(day); dayStart.setHours(0,0,0,0);
  const dayEnd   = new Date(day); dayEnd.setHours(23,59,59,999);
  // Para eventos dia_todo: comparar só a parte da data (sem timezone)
  if (ev.dia_todo) {
    const ini = parseDateLocal(ev.data_inicio);
    const fim = parseDateLocal(ev.data_fim);
    return ini <= dayEnd && fim >= dayStart;
  }
  const ini = new Date(ev.data_inicio);
  const fim = new Date(ev.data_fim);
  return ini <= dayEnd && fim >= dayStart;
}
function getStatusInfo(val) {
  return STATUS_LIST.find(s => s.val === val) || STATUS_LIST[0];
}
function hexToRgba(hex, alpha) {
  const r = parseInt(hex.slice(1,3),16);
  const g = parseInt(hex.slice(3,5),16);
  const b = parseInt(hex.slice(5,7),16);
  return `rgba(${r},${g},${b},${alpha})`;
}

// ─── Estilos base ─────────────────────────────────────────────────────────────
const S = {
  page: {
    background: C.bg,
    minHeight: '100vh',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif",
    color: C.text,
  },
  header: {
    background: C.surface,
    borderBottom: `1px solid ${C.border}`,
    padding: '16px 20px',
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    flexWrap: 'wrap',
  },
  title: {
    fontSize: 20,
    fontWeight: 700,
    color: C.navy,
    margin: 0,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  navBtn: {
    background: 'none',
    border: `1px solid ${C.border}`,
    borderRadius: 8,
    padding: '6px 12px',
    cursor: 'pointer',
    fontSize: 16,
    color: C.navy,
    display: 'flex',
    alignItems: 'center',
  },
  toggleGroup: {
    display: 'flex',
    border: `1px solid ${C.border}`,
    borderRadius: 8,
    overflow: 'hidden',
  },
  toggleBtn: (active) => ({
    background: active ? C.navy : C.surface,
    color: active ? '#fff' : C.muted,
    border: 'none',
    padding: '6px 14px',
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: active ? 600 : 400,
    transition: 'all .15s',
  }),
  btnPrimary: {
    background: C.orange,
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    padding: '8px 16px',
    cursor: 'pointer',
    fontSize: 14,
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },
  monthLabel: {
    fontSize: 18,
    fontWeight: 700,
    color: C.navy,
    minWidth: 200,
    textAlign: 'center',
  },
  // Calendário mensal
  calGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(7, 1fr)',
    border: `1px solid ${C.border}`,
    borderRadius: 12,
    overflow: 'hidden',
    background: C.surface,
    margin: 16,
  },
  calHeaderCell: {
    background: C.navy,
    color: '#fff',
    textAlign: 'center',
    padding: '10px 4px',
    fontSize: 12,
    fontWeight: 600,
    letterSpacing: '.5px',
  },
  calCell: (isToday, isCurrentMonth, isWeekend) => ({
    height: 120,
    minHeight: 120,
    maxHeight: 120,
    overflow: 'hidden',
    padding: '6px 4px 4px',
    borderRight: `1px solid ${C.border}`,
    borderBottom: `1px solid ${C.border}`,
    background: isToday ? hexToRgba(C.orange, 0.06)
               : isCurrentMonth ? C.surface
               : '#f9fafb',
    cursor: 'pointer',
    transition: 'background .12s',
    position: 'relative',
    boxSizing: 'border-box',
  }),
  calDayNum: (isToday, isCurrentMonth) => ({
    fontSize: 13,
    fontWeight: isToday ? 700 : 500,
    color: isToday ? C.orange : isCurrentMonth ? C.navy : C.muted,
    background: isToday ? hexToRgba(C.orange, 0.15) : 'transparent',
    borderRadius: '50%',
    width: 26,
    height: 26,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 4,
  }),
  evChip: (cor) => ({
    background: hexToRgba(cor, 0.18),
    borderLeft: `3px solid ${cor}`,
    color: cor,
    fontSize: 11,
    fontWeight: 600,
    padding: '2px 5px',
    borderRadius: '0 4px 4px 0',
    marginBottom: 2,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    cursor: 'pointer',
    display: 'block',
    width: '100%',
    boxSizing: 'border-box',
  }),
  // Semana
  weekGrid: {
    display: 'grid',
    gridTemplateColumns: '60px repeat(7, 1fr)',
    border: `1px solid ${C.border}`,
    borderRadius: 12,
    overflow: 'hidden',
    background: C.surface,
    margin: 16,
  },
  weekHeaderCell: (isToday) => ({
    background: isToday ? C.navy : '#f0f3f7',
    color: isToday ? '#fff' : C.navy,
    textAlign: 'center',
    padding: '10px 4px',
    fontSize: 12,
    fontWeight: 600,
    borderLeft: `1px solid ${C.border}`,
    borderBottom: `1px solid ${C.border}`,
  }),
  weekTimeCell: {
    background: '#f9fafb',
    color: C.muted,
    fontSize: 11,
    textAlign: 'right',
    padding: '0 6px',
    borderBottom: `1px solid ${C.border}`,
    height: 48,
    display: 'flex',
    alignItems: 'flex-start',
    paddingTop: 4,
  },
  weekDayCell: (isToday) => ({
    borderLeft: `1px solid ${C.border}`,
    borderBottom: `1px solid ${C.border}`,
    height: 48,
    position: 'relative',
    background: isToday ? hexToRgba(C.orange, 0.04) : C.surface,
    cursor: 'pointer',
  }),
};

// ─── Modal de Evento ──────────────────────────────────────────────────────────
function ModalEvento({ evento, usuarios, onSave, onDelete, onClose }) {
  const isEdicao = !!evento?.id && !evento?._criarComoNovo;
  const hoje = new Date();
  const pad = n => String(n).padStart(2,'0');
  const defaultInicio = `${hoje.getFullYear()}-${pad(hoje.getMonth()+1)}-${pad(hoje.getDate())}T${pad(hoje.getHours())}:00`;
  const defaultFim    = `${hoje.getFullYear()}-${pad(hoje.getMonth()+1)}-${pad(hoje.getDate())}T${pad(hoje.getHours()+1)}:00`;

  const [form, setForm] = useState({
    titulo:        evento?.titulo || '',
    descricao:     evento?.descricao || '',
    endereco:      evento?.endereco || '',
    tecnicos: evento?.tecnicos_ids
      ? evento.tecnicos_ids.map((id,i) => ({ id, nome: (evento.tecnicos_nomes||[])[i]||'' }))
      : (evento?.tecnico_id ? [{ id: evento.tecnico_id, nome: evento.tecnico_nome||'' }] : []),
    data_inicio:   evento?.data_inicio ? fmtDatetimeLocal(evento.data_inicio) : defaultInicio,
    data_fim:      evento?.data_fim    ? fmtDatetimeLocal(evento.data_fim)    : defaultFim,
    dia_todo:      evento?.dia_todo || false,
    dia_inicio:    evento?.dia_todo && evento?.data_inicio
      ? fmtDateInput(evento.data_inicio)
      : evento?.data_inicio
        ? evento.data_inicio.substring(0, 10)   // data clicada (vem como "YYYY-MM-DDTHH:mm")
        : hoje.toISOString().split('T')[0],
    dia_fim:       evento?.dia_todo && evento?.data_fim
      ? fmtDateInput(evento.data_fim)
      : evento?.data_inicio
        ? evento.data_inicio.substring(0, 10)   // mesma data do início
        : hoje.toISOString().split('T')[0],
    cor:           evento?.cor || '#D85A30',
    status:        evento?.status || 'agendado',
    recorrencia:   evento?.recorrencia || 'nenhuma',
    rec_fim:       evento?.rec_fim ? fmtDateInput(evento.rec_fim) : '',
  });
  const [saving, setSaving] = useState(false);
  const [erro, setErro] = useState('');

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const toggleTecnico = (id) => {
    if (!id) return;
    const u = usuarios.find(u => String(u.id) === String(id));
    if (!u) return;
    const jatem = form.tecnicos.some(t => String(t.id) === String(id));
    if (jatem) {
      set('tecnicos', form.tecnicos.filter(t => String(t.id) !== String(id)));
    } else {
      set('tecnicos', [...form.tecnicos, { id: u.id, nome: u.nome }]);
    }
  };

  const handleSave = async () => {
    if (!form.titulo.trim()) { setErro('Título é obrigatório.'); return; }
    setSaving(true); setErro('');
    try {
      let data_inicio, data_fim;
      if (form.dia_todo) {
        data_inicio = form.dia_inicio;
        data_fim    = form.dia_fim;
      } else {
        const toISOLocal = (dt) => {
          if (!dt) return dt;
          const d = new Date(dt);
          return d.toISOString();
        };
        data_inicio = toISOLocal(form.data_inicio);
        data_fim    = toISOLocal(form.data_fim);
      }
      if (new Date(data_fim) < new Date(data_inicio)) {
        setErro('Data de fim não pode ser anterior ao início.'); setSaving(false); return;
      }
      const payload = {
        titulo: form.titulo, descricao: form.descricao, endereco: form.endereco,
        tecnico_id:      form.tecnicos[0]?.id   || null,
        tecnico_nome:    form.tecnicos[0]?.nome || null,
        tecnicos_ids:    form.tecnicos.map(t => t.id),
        tecnicos_nomes:  form.tecnicos.map(t => t.nome),
        data_inicio, data_fim, dia_todo: form.dia_todo, cor: form.cor, status: form.status,
        recorrencia: form.recorrencia || 'nenhuma',
        rec_fim: form.recorrencia !== 'nenhuma' && form.rec_fim ? form.rec_fim : null,
      };

      if (evento?._criarComoNovo) {
        // Cria evento independente
        await api.post('/agenda', { ...payload, recorrencia: 'nenhuma', rec_fim: null });
        // Registra exceção no evento original para remover a ocorrência da série
        if (evento._idOriginalExcecao && evento._dataExcecao) {
          await api.post(`/agenda/${evento._idOriginalExcecao}/excecao`, {
            data_excecao: evento._dataExcecao,
          });
        }
      } else if (isEdicao) {
        const idReal = evento._idOriginal || evento.id;
        await api.patch(`/agenda/${idReal}`, payload);
      } else {
        await api.post('/agenda', payload);
      }
      onSave();
    } catch (e) {
      setErro(e.response?.data?.erro || 'Erro ao salvar evento.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Excluir este evento?')) return;
    try {
      // Sempre usar o ID original — IDs virtuais (ex: "55_tec_0") causam 500
      const idReal = evento._idOriginal || String(evento.id).split('_')[0];
      await api.delete(`/agenda/${idReal}`);
      onDelete();
    } catch { setErro('Erro ao excluir.'); }
  };

  const inp = {
    width: '100%', padding: '9px 12px', borderRadius: 8, fontSize: 14,
    border: `1px solid ${C.border}`, background: '#f9fafb', color: C.text,
    outline: 'none', boxSizing: 'border-box',
  };
  const label = { fontSize: 12, fontWeight: 600, color: C.muted, marginBottom: 4, display: 'block', textTransform: 'uppercase', letterSpacing: '.4px' };

  return (
    <div style={{ position:'fixed',inset:0,background:'rgba(0,0,0,.45)',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center',padding:16 }}>
      <div style={{ background:C.surface,borderRadius:16,width:'100%',maxWidth:560,maxHeight:'90vh',overflowY:'auto',boxShadow:'0 20px 60px rgba(0,0,0,.25)' }}>
        {/* Header */}
        <div style={{ background:C.navy,borderRadius:'16px 16px 0 0',padding:'18px 24px',display:'flex',alignItems:'center',justifyContent:'space-between' }}>
          <span style={{ color:'#fff',fontWeight:700,fontSize:16 }}>
            {evento?._criarComoNovo ? '📅 Editar esta Ocorrência' : isEdicao ? '✏️ Editar Evento' : '📅 Novo Evento'}
          </span>
          <button onClick={onClose} style={{ background:'none',border:'none',color:'#fff',fontSize:20,cursor:'pointer',lineHeight:1 }}>×</button>
        </div>

        <div style={{ padding:24, display:'flex', flexDirection:'column', gap:18 }}>

          {/* Título */}
          <div>
            <label style={label}>Título *</label>
            <input style={inp} value={form.titulo} onChange={e=>set('titulo',e.target.value)} placeholder="Ex: Visita técnica, Reunião..." />
          </div>



          {/* Dia todo toggle */}
          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
            <button onClick={()=>set('dia_todo',!form.dia_todo)} style={{
              width:44, height:24, borderRadius:12, border:'none', cursor:'pointer',
              background: form.dia_todo ? C.orange : C.border,
              position:'relative', transition:'background .2s', flexShrink:0,
            }}>
              <span style={{
                position:'absolute', top:3, left: form.dia_todo?20:3,
                width:18, height:18, borderRadius:'50%', background:'#fff',
                transition:'left .2s', display:'block',
              }}/>
            </button>
            <span style={{ fontSize:14, color:C.text, fontWeight:500 }}>Dia todo</span>
          </div>

          {/* Datas */}
          {form.dia_todo ? (
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
              <div>
                <label style={label}>Data início</label>
                <input type="date" style={inp} value={form.dia_inicio} onChange={e=>set('dia_inicio',e.target.value)} />
              </div>
              <div>
                <label style={label}>Data fim</label>
                <input type="date" style={inp} value={form.dia_fim} onChange={e=>set('dia_fim',e.target.value)} />
              </div>
            </div>
          ) : (
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
              <div>
                <label style={label}>Início</label>
                <input type="datetime-local" style={inp} value={form.data_inicio} onChange={e=>set('data_inicio',e.target.value)} />
              </div>
              <div>
                <label style={label}>Fim</label>
                <input type="datetime-local" style={inp} value={form.data_fim} onChange={e=>set('data_fim',e.target.value)} />
              </div>
            </div>
          )}

          {/* Técnicos — múltipla seleção */}
          <div>
            <label style={label}>Técnicos designados</label>
            {/* Chips dos selecionados */}
            {form.tecnicos.length > 0 && (
              <div style={{ display:'flex', flexWrap:'wrap', gap:6, marginBottom:8 }}>
                {form.tecnicos.map(t => (
                  <span key={t.id} style={{ display:'inline-flex', alignItems:'center', gap:4, background:C.navyL, color:'#fff', borderRadius:20, padding:'3px 10px', fontSize:12, fontWeight:600 }}>
                    👤 {t.nome}
                    <button onClick={()=>toggleTecnico(t.id)} style={{ background:'none', border:'none', color:'#fff', cursor:'pointer', fontSize:14, lineHeight:1, padding:'0 0 0 4px' }}>×</button>
                  </span>
                ))}
              </div>
            )}
            <select style={inp} value="" onChange={e=>toggleTecnico(e.target.value)}>
              <option value="">+ Adicionar técnico...</option>
              {usuarios.filter(u => !form.tecnicos.some(t => String(t.id) === String(u.id))).map(u=>(
                <option key={u.id} value={u.id}>{u.nome}</option>
              ))}
            </select>
          </div>

          {/* Endereço */}
          <div>
            <label style={label}>Endereço</label>
            <input style={inp} value={form.endereco} onChange={e=>set('endereco',e.target.value)} placeholder="Rua, número, bairro, cidade..." />
          </div>

          {/* Descrição com formatação */}
          <div>
            <label style={label}>Descrição</label>
            <div style={{ border:`1px solid ${C.border}`, borderRadius:8, overflow:'hidden', background:'#f9fafb' }}>
              {/* Toolbar */}
              <div style={{ display:'flex', gap:2, padding:'6px 8px', borderBottom:`1px solid ${C.border}`, background:C.surface, flexWrap:'wrap' }}>
                {[
                  { cmd:'bold',          icon:'B',  style:{ fontWeight:700 } },
                  { cmd:'italic',        icon:'I',  style:{ fontStyle:'italic' } },
                  { cmd:'underline',     icon:'U',  style:{ textDecoration:'underline' } },
                  { cmd:'insertOrderedList',   icon:'1≡', style:{} },
                  { cmd:'insertUnorderedList', icon:'•≡', style:{} },
                ].map(btn => (
                  <button key={btn.cmd}
                    type="button"
                    onMouseDown={e => { e.preventDefault(); document.execCommand(btn.cmd, false, null); }}
                    style={{ background:'none', border:`1px solid transparent`, borderRadius:6, padding:'3px 8px', cursor:'pointer', fontSize:13, color:C.navy, ...btn.style,
                      minWidth:30, display:'flex', alignItems:'center', justifyContent:'center' }}
                    onMouseEnter={e => { e.currentTarget.style.background=C.bg; e.currentTarget.style.borderColor=C.border; }}
                    onMouseLeave={e => { e.currentTarget.style.background='none'; e.currentTarget.style.borderColor='transparent'; }}>
                    {btn.icon}
                  </button>
                ))}
                <div style={{ width:1, height:20, background:C.border, margin:'0 4px', alignSelf:'center' }} />
                <button type="button"
                  onMouseDown={e => { e.preventDefault(); document.execCommand('removeFormat', false, null); }}
                  style={{ background:'none', border:`1px solid transparent`, borderRadius:6, padding:'3px 8px', cursor:'pointer', fontSize:13, color:C.muted, textDecoration:'line-through' }}
                  onMouseEnter={e => { e.currentTarget.style.background=C.bg; }}
                  onMouseLeave={e => { e.currentTarget.style.background='none'; }}>
                  Tx
                </button>
              </div>
              {/* Editor — usa ref para evitar bug de texto invertido */}
              <RichEditor
                value={form.descricao || ''}
                onChange={v => set('descricao', v)}
                placeholder="Adicionar uma descrição..."
              />
            </div>
            <style>{`[contenteditable]:empty:before { content: attr(data-placeholder); color: #aaa; pointer-events: none; }`}</style>
          </div>

          {/* Recorrência */}
          <div>
            <label style={label}>Recorrência</label>
            <select style={inp} value={form.recorrencia} onChange={e=>set('recorrencia',e.target.value)}>
              <option value="nenhuma">Sem recorrência</option>
              <option value="diaria">Diária</option>
              <option value="semanal">Semanal</option>
              <option value="quinzenal">Quinzenal</option>
              <option value="mensal">Mensal</option>
              <option value="anual">Anual</option>
            </select>
            {form.recorrencia !== 'nenhuma' && (
              <div style={{ marginTop:10 }}>
                <label style={{ ...label, marginBottom:4 }}>Repetir até</label>
                <input type="date" style={inp} value={form.rec_fim} onChange={e=>set('rec_fim',e.target.value)}
                  min={form.dia_todo ? form.dia_inicio : (form.data_inicio||'').substring(0,10)} />
                <div style={{ fontSize:11, color:C.muted, marginTop:4 }}>
                  {form.recorrencia === 'diaria' && '⟳ Repete todo dia'}
                  {form.recorrencia === 'semanal' && '⟳ Repete toda semana no mesmo dia'}
                  {form.recorrencia === 'quinzenal' && '⟳ Repete a cada 15 dias'}
                  {form.recorrencia === 'mensal' && '⟳ Repete todo mês na mesma data'}
                  {form.recorrencia === 'anual' && '⟳ Repete todo ano na mesma data'}
                  {form.rec_fim && ` até ${new Date(form.rec_fim+'T12:00:00').toLocaleDateString('pt-BR')}`}
                </div>
              </div>
            )}
          </div>

          {erro && <div style={{ background:C.vermelhoL, color:C.vermelho, borderRadius:8, padding:'10px 14px', fontSize:13, fontWeight:500 }}>{erro}</div>}

          {/* Ações */}
          <div style={{ display:'flex', gap:10, justifyContent:'flex-end', flexWrap:'wrap', marginTop:4 }}>
            {isEdicao && (
              <button onClick={handleDelete} style={{ background:C.vermelhoL,color:C.vermelho,border:`1px solid ${C.vermelho}`,borderRadius:8,padding:'8px 16px',cursor:'pointer',fontSize:14,fontWeight:600 }}>
                🗑️ Excluir
              </button>
            )}
            <button onClick={onClose} style={{ background:C.surface,color:C.muted,border:`1px solid ${C.border}`,borderRadius:8,padding:'8px 16px',cursor:'pointer',fontSize:14 }}>
              Cancelar
            </button>
            <button onClick={handleSave} disabled={saving} style={{ ...S.btnPrimary, opacity:saving?.7:1 }}>
              {saving ? 'Salvando...' : isEdicao ? '✓ Salvar' : '+ Criar Evento'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Card de detalhe do evento (clique no chip) ───────────────────────────────
function CardDetalhe({ evento, onEdit, onClose }) {
  const st = getStatusInfo(evento.status);
  return (
    <div style={{ position:'fixed',inset:0,background:'rgba(0,0,0,.4)',zIndex:999,display:'flex',alignItems:'center',justifyContent:'center',padding:16 }}>
      <div style={{ background:C.surface,borderRadius:14,width:'100%',maxWidth:400,boxShadow:'0 16px 48px rgba(0,0,0,.2)',overflow:'hidden' }}>
        <div style={{ background:corDoEvento(evento, {}),padding:'16px 20px',display:'flex',alignItems:'center',justifyContent:'space-between' }}>
          <span style={{ color:'#fff',fontWeight:700,fontSize:15 }}>{evento.titulo}</span>
          <button onClick={onClose} style={{ background:'none',border:'none',color:'#fff',fontSize:20,cursor:'pointer' }}>×</button>
        </div>
        <div style={{ padding:20, display:'flex', flexDirection:'column', gap:12 }}>

          {evento.dia_todo ? (
            <Row icon="📆" label="Período">
              {fmtData(evento.data_inicio)}
              {evento.data_inicio.substring(0,10) !== evento.data_fim.substring(0,10) && ` → ${fmtData(evento.data_fim)}`}
              {' · '}Dia todo
            </Row>
          ) : (
            <Row icon="🕐" label="Horário">
              {fmtData(evento.data_inicio)} {fmtHora(evento.data_inicio)} → {fmtHora(evento.data_fim)}
            </Row>
          )}
          {evento.recorrencia && evento.recorrencia !== 'nenhuma' && (
            <Row icon="⟳" label="Recorrência">
              {{ diaria:'Diária', semanal:'Semanal', quinzenal:'Quinzenal', mensal:'Mensal', anual:'Anual' }[evento.recorrencia]}
              {evento.rec_fim && ` até ${new Date(evento.rec_fim+'T12:00:00').toLocaleDateString('pt-BR')}`}
            </Row>
          )}
          {(evento.tecnicos_nomes?.length > 0 || evento.tecnico_nome) && (
            <Row icon="👤" label={evento.tecnicos_nomes?.length > 1 ? 'Técnicos' : 'Técnico'}>
              {(evento.tecnicos_nomes?.length > 0 ? evento.tecnicos_nomes : [evento.tecnico_nome]).join(', ')}
            </Row>
          )}
          {evento.endereco && <Row icon="📍" label="Endereço">{evento.endereco}</Row>}
          {evento.descricao && (
            <Row icon="📝" label="Descrição">
              <div dangerouslySetInnerHTML={{ __html: evento.descricao }}
                style={{ fontSize:14, color:C.text, lineHeight:1.6 }} />
            </Row>
          )}
          {evento.criado_por_nome && <Row icon="✍️" label="Criado por">{evento.criado_por_nome}</Row>}
          <div style={{ display:'flex', gap:8, marginTop:8 }}>
            <button onClick={onClose} style={{ flex:1,background:C.surface,color:C.muted,border:`1px solid ${C.border}`,borderRadius:8,padding:'8px',cursor:'pointer',fontSize:13 }}>Fechar</button>
            <button onClick={onEdit} style={{ flex:1,background:C.navy,color:'#fff',border:'none',borderRadius:8,padding:'8px',cursor:'pointer',fontSize:13,fontWeight:600 }}>✏️ Editar</button>
          </div>
        </div>
      </div>
    </div>
  );
}
function Row({ icon, label, children }) {
  return (
    <div style={{ display:'flex', gap:10, alignItems:'flex-start' }}>
      <span style={{ fontSize:16, flexShrink:0 }}>{icon}</span>
      <div>
        <div style={{ fontSize:11, color:C.muted, fontWeight:600, textTransform:'uppercase', letterSpacing:'.4px' }}>{label}</div>
        <div style={{ fontSize:14, color:C.text, marginTop:2 }}>{children}</div>
      </div>
    </div>
  );
}

// ─── Visão Mensal ─────────────────────────────────────────────────────────────
function ViewMensal({ eventos, ano, mes, hoje, onDayClick, onEventClick, dragEvId, setDragEvId, onDrop, onMaisClick, usuariosCores }) {
  const firstDay = new Date(ano, mes, 1).getDay();
  const daysInMonth = new Date(ano, mes+1, 0).getDate();
  const daysInPrevMonth = new Date(ano, mes, 0).getDate();

  const cells = [];
  // Dias do mês anterior
  for (let i = firstDay - 1; i >= 0; i--) {
    cells.push({ day: daysInPrevMonth - i, month: mes - 1, current: false });
  }
  // Dias do mês atual
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push({ day: d, month: mes, current: true });
  }
  // Dias do mês seguinte
  const rem = 42 - cells.length;
  for (let d = 1; d <= rem; d++) {
    cells.push({ day: d, month: mes + 1, current: false });
  }

  return (
    <div style={S.calGrid}>
      {DIAS_SEMANA_SHORT.map(d => (
        <div key={d} style={S.calHeaderCell}>{d}</div>
      ))}
      {cells.map((cell, i) => {
        const cellDate = new Date(ano, cell.month, cell.day);
        const isToday = isSameDay(cellDate, hoje);
        const isWeekend = cellDate.getDay() === 0 || cellDate.getDay() === 6;
        const evsDia = eventos.filter(ev => eventOnDay(ev, cellDate));

        return (
          <div key={i}
            style={{ ...S.calCell(isToday, cell.current, isWeekend), outline: dragEvId ? '2px dashed transparent' : 'none' }}
            onClick={() => { if (!dragEvId) onDayClick(cellDate); }}
            onDragOver={e => { e.preventDefault(); e.currentTarget.style.outline='2px dashed #D85A30'; e.currentTarget.style.background='rgba(216,90,48,0.08)'; }}
            onDragLeave={e => { e.currentTarget.style.outline='none'; e.currentTarget.style.background=''; }}
            onDrop={e => { e.preventDefault(); e.currentTarget.style.outline='none'; e.currentTarget.style.background=''; onDrop(cellDate); }}>
            <div style={S.calDayNum(isToday, cell.current)}>
              {cell.day}
            </div>
            {evsDia.slice(0,3).map(ev => (
              <div key={ev.id}
                draggable
                onDragStart={e => { e.stopPropagation(); setDragEvId(ev.id); e.dataTransfer.effectAllowed='move'; }}
                onDragEnd={() => setDragEvId(null)}
                style={{ ...S.evChip(corDoEvento(ev, usuariosCores)), opacity: dragEvId===ev.id ? 0.45 : 1, cursor:'grab' }}
                onClick={e => { e.stopPropagation(); onEventClick(ev); }}>
                {ev.recorrencia && ev.recorrencia !== 'nenhuma' ? '⟳ ' : ''}
                {ev.dia_todo ? '◆ ' : fmtHora(ev.data_inicio)+' '}{ev.titulo}
              </div>
            ))}
            {evsDia.length > 3 && (
              <div
                onClick={e => { e.stopPropagation(); onMaisClick(cellDate, evsDia); }}
                style={{ fontSize:10, color:C.orange, fontWeight:700, padding:'2px 4px', cursor:'pointer', borderRadius:4, userSelect:'none' }}
                onMouseEnter={e => e.currentTarget.style.background=C.orangeL}
                onMouseLeave={e => e.currentTarget.style.background='transparent'}>
                +{evsDia.length-3} mais
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Visão Semanal ────────────────────────────────────────────────────────────
function ViewSemanal({ eventos, semanaInicio, hoje, onSlotClick, onEventClick }) {
  const HOURS = Array.from({length:24},(_,i)=>i);
  const days = Array.from({length:7},(_,i)=>{
    const d = new Date(semanaInicio);
    d.setDate(d.getDate()+i);
    return d;
  });

  // Eventos de dia todo
  const evsDiaTodo = eventos.filter(ev => ev.dia_todo && days.some(d => eventOnDay(ev, d)));

  return (
    <div style={{ margin:16 }}>
      {/* Eventos dia todo */}
      {evsDiaTodo.length > 0 && (
        <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:10, padding:'10px 12px', marginBottom:8 }}>
          <span style={{ fontSize:11, fontWeight:700, color:C.muted, textTransform:'uppercase', letterSpacing:'.5px' }}>Dia todo</span>
          <div style={{ display:'flex', flexWrap:'wrap', gap:6, marginTop:6 }}>
            {evsDiaTodo.map(ev => (
              <div key={ev.id} onClick={()=>onEventClick(ev)}
                style={{ ...S.evChip(corDoEvento(ev, {})), fontSize:12, padding:'4px 10px', cursor:'pointer' }}>
                {ev.titulo} · {days.filter(d=>eventOnDay(ev,d)).map(d=>DIAS_SEMANA_SHORT[d.getDay()]).join(', ')}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Grade horária */}
      <div style={{ ...S.weekGrid, display:'grid', gridTemplateColumns:'60px repeat(7, 1fr)', border:`1px solid ${C.border}`, borderRadius:12, overflow:'hidden', background:C.surface }}>
        {/* Header vazio */}
        <div style={{ background:'#f0f3f7', borderBottom:`1px solid ${C.border}`, height:48 }} />
        {days.map((d, i) => {
          const isToday = isSameDay(d, hoje);
          return (
            <div key={i} style={S.weekHeaderCell(isToday)}>
              <div style={{ fontSize:11, opacity:.8 }}>{DIAS_SEMANA_SHORT[d.getDay()]}</div>
              <div style={{ fontSize:18, fontWeight:700 }}>{d.getDate()}</div>
            </div>
          );
        })}

        {/* Linhas de hora */}
        {HOURS.map(h => (
          <React.Fragment key={h}>
            <div style={S.weekTimeCell}>
              {h === 0 ? '' : `${String(h).padStart(2,'0')}:00`}
            </div>
            {days.map((d, di) => {
              const isToday = isSameDay(d, hoje);
              // Eventos que passam por este slot (hora h, dia d)
              const evsSlot = eventos.filter(ev => {
                if (ev.dia_todo) return false;
                const ini = new Date(ev.data_inicio);
                const fim = new Date(ev.data_fim);
                const slotIni = new Date(d); slotIni.setHours(h,0,0,0);
                const slotFim = new Date(d); slotFim.setHours(h,59,59,999);
                return ini <= slotFim && fim >= slotIni;
              });
              return (
                <div key={di} style={S.weekDayCell(isToday)}
                  onClick={()=>{ const dt=new Date(d); dt.setHours(h); onSlotClick(dt); }}>
                  {evsSlot.map(ev => {
                    const ini = new Date(ev.data_inicio);
                    const isStart = isSameDay(ini, d) && ini.getHours() === h;
                    if (!isStart) return null;
                    const dur = Math.max(1, (new Date(ev.data_fim)-ini) / 3600000);
                    return (
                      <div key={ev.id} onClick={e=>{ e.stopPropagation(); onEventClick(ev); }}
                        style={{
                          position:'absolute', left:2, right:2, top:2,
                          height: Math.min(dur * 48 - 4, 48 * 24),
                          background: hexToRgba(corDoEvento(ev, {}), 0.18),
                          borderLeft: `3px solid ${corDoEvento(ev, {})}`,
                          borderRadius: '0 6px 6px 0',
                          padding:'2px 6px',
                          fontSize:11, fontWeight:600, color:corDoEvento(ev, {}),
                          overflow:'hidden', zIndex:2, cursor:'pointer',
                        }}>
                        {ev.titulo}
                        {dur >= 1 && <div style={{ fontSize:10, opacity:.8 }}>{fmtHora(ev.data_inicio)}</div>}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

// ─── Painel lateral: eventos de hoje ─────────────────────────────────────────
function PainelHoje({ eventos, onEventClick }) {
  if (eventos.length === 0) return null;
  return (
    <div style={{
      background: C.surface, border:`1px solid ${C.border}`, borderRadius:12,
      padding:'12px 16px', marginBottom:4,
    }}>
      <div style={{ fontWeight:700, fontSize:13, color:C.navy, marginBottom:10, display:'flex', alignItems:'center', gap:6 }}>
        <span style={{ background:C.orange, color:'#fff', borderRadius:'50%', width:20, height:20, display:'inline-flex', alignItems:'center', justifyContent:'center', fontSize:11, fontWeight:700 }}>{eventos.length}</span>
        Hoje
      </div>
      <div style={{ display:'flex', flexWrap:'wrap', gap:10 }}>
        {eventos.map(ev => (
          <div key={ev.id} onClick={()=>onEventClick(ev)}
            style={{ borderLeft:`3px solid ${corDoEvento(ev, {})}`, paddingLeft:10, cursor:'pointer', minWidth:160, flex:'1 1 160px' }}>
            <div style={{ fontSize:13, fontWeight:600, color:C.navy }}>{ev.titulo}</div>
            <div style={{ fontSize:11, color:C.muted, marginTop:2 }}>
              {ev.dia_todo ? 'Dia todo' : `${fmtHora(ev.data_inicio)} – ${fmtHora(ev.data_fim)}`}
            </div>
            {(ev.tecnicos_nomes?.length > 0 ? ev.tecnicos_nomes : ev.tecnico_nome ? [ev.tecnico_nome] : []).map((n,i) => (
              <div key={i} style={{ fontSize:11, color:C.muted }}>👤 {n}</div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Componente principal ─────────────────────────────────────────────────────

// ─── Expande eventos recorrentes para o período visível ──────────────────────

// ─── Expande evento com múltiplos técnicos em 1 chip por técnico ─────────────
function expandirPorTecnico(eventos) {
  const result = [];
  for (const ev of eventos) {
    const nomes = ev.tecnicos_nomes || (ev.tecnico_nome ? [ev.tecnico_nome] : []);
    const ids   = ev.tecnicos_ids   || (ev.tecnico_id   ? [ev.tecnico_id]   : []);
    if (nomes.length <= 1) {
      result.push(ev);
      continue;
    }
    nomes.forEach((nome, i) => {
      result.push({
        ...ev,
        id: `${ev.id}_tec_${i}`,
        _multiTecnico: true,
        _idOriginal: ev._idOriginal || ev.id,
        tecnico_nome: nome,
        tecnico_id:   ids[i] || null,
      });
    });
  }
  return result;
}

function expandirRecorrencia(eventos, ano, mes) {
  const result = [];
  const mesInicio = new Date(ano, mes, 1);
  const mesFim    = new Date(ano, mes + 1, 0, 23, 59, 59);
  const pad = x => String(x).padStart(2, '0');
  const fmtD = d => `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}`;

  for (const ev of eventos) {
    if (!ev.recorrencia || ev.recorrencia === 'nenhuma') {
      result.push(ev);
      continue;
    }

    // Datas excluídas desta série (vêm do backend como array de strings 'YYYY-MM-DD')
    const excecoes = new Set(ev.excecoes || []);

    const oriIni = new Date(ev.data_inicio);
    const oriFim = new Date(ev.data_fim);
    const oriDurMs = oriFim - oriIni;

    const recFimDate = ev.rec_fim
      ? new Date(ev.rec_fim.substring(0, 10) + 'T23:59:59')
      : new Date(ano + 5, 11, 31);

    const proximaOcorrencia = (base, n) => {
      const d = new Date(base);
      const h = base.getHours();
      const m = base.getMinutes();
      if (ev.recorrencia === 'diaria')    d.setDate(d.getDate() + n);
      if (ev.recorrencia === 'semanal')   d.setDate(d.getDate() + n * 7);
      if (ev.recorrencia === 'quinzenal') d.setDate(d.getDate() + n * 15);
      if (ev.recorrencia === 'mensal')    d.setMonth(d.getMonth() + n);
      if (ev.recorrencia === 'anual')     d.setFullYear(d.getFullYear() + n);
      d.setHours(h, m, 0, 0);
      return d;
    };

    let nInicio = 0;
    if (ev.recorrencia === 'mensal') {
      nInicio = Math.max(0, (ano - oriIni.getFullYear()) * 12 + (mes - oriIni.getMonth()) - 1);
    } else if (ev.recorrencia === 'anual') {
      nInicio = Math.max(0, ano - oriIni.getFullYear() - 1);
    }

    // Inclui o evento original se cai no mês e não tem exceção nessa data
    const oriCaiNoMes = oriIni <= mesFim && oriFim >= mesInicio;
    const oriDataStr = fmtD(oriIni);
    if (oriCaiNoMes && !excecoes.has(oriDataStr)) result.push(ev);

    // Gera as ocorrências recorrentes
    let n = nInicio;
    let tentativas = 0;
    while (tentativas < 1000) {
      n++;
      tentativas++;
      const novaIni = proximaOcorrencia(oriIni, n);

      if (novaIni > recFimDate) break;
      if (novaIni > mesFim) break;

      const novaFim = new Date(novaIni.getTime() + oriDurMs);
      const novaDataStr = fmtD(novaIni);

      // Pula datas que têm exceção
      if (excecoes.has(novaDataStr)) continue;

      if (novaIni <= mesFim && novaFim >= mesInicio) {
        result.push({
          ...ev,
          id: `${ev.id}_rec_${n}`,
          _recorrente: true,
          _idOriginal: ev.id,
          _dataOcorrencia: novaDataStr, // guarda a data da ocorrência para exceções
          data_inicio: ev.dia_todo ? novaDataStr : novaIni.toISOString(),
          data_fim:    ev.dia_todo ? fmtD(novaFim) : novaFim.toISOString(),
        });
      }
    }
  }
  return result;
}


// Componente isolado para evitar bug de texto invertido no contentEditable
function RichEditor({ value, onChange, placeholder }) {
  const ref = useRef(null);
  const lastValue = useRef(value);

  // Só sincroniza o DOM quando o valor externo muda E é diferente do que já está no DOM
  // Isso evita resetar o cursor enquanto o usuário digita
  useEffect(() => {
    if (ref.current && value !== lastValue.current) {
      lastValue.current = value;
      ref.current.innerHTML = value;
    }
  }, [value]);

  // Inicializa conteúdo na montagem
  useEffect(() => {
    if (ref.current) {
      ref.current.innerHTML = value || '';
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleInput = (e) => {
    const html = e.currentTarget.innerHTML;
    lastValue.current = html;
    onChange(html);
  };

  return (
    <div
      ref={ref}
      contentEditable
      suppressContentEditableWarning
      onInput={handleInput}
      data-placeholder={placeholder}
      style={{ minHeight: 100, padding: '10px 12px', fontSize: 14, outline: 'none', background: '#f9fafb', lineHeight: 1.6 }}
    />
  );
}

export default function Agenda({ usuario, toast }) {
  const hoje = new Date();
  const [view, setView] = useState('mensal'); // mensal | semanal
  const [ano, setAno] = useState(hoje.getFullYear());
  const [mes, setMes] = useState(hoje.getMonth());
  const [semanaInicio, setSemanaInicio] = useState(startOfWeek(hoje));
  const [eventos, setEventos] = useState([]);
  const [eventosHoje, setEventosHoje] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState(null);      // null | { type:'novo', data? } | { type:'editar', evento } | { type:'detalhe', evento }
  const [filtroTecnico, setFiltroTecnico] = useState('');
  const [filtroStatus, setFiltroStatus] = useState('');
  const [dragEvId, setDragEvId] = useState(null);
  const [popoverDia, setPopoverDia] = useState(null);
  const [modalDropRec, setModalDropRec] = useState(null); // { ev, novaIni, novaFim, realId }

  // Buscar eventos
  const carregarEventos = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ mes: mes+1, ano });
      if (filtroTecnico) params.append('tecnico_id', filtroTecnico);
      if (filtroStatus)  params.append('status', filtroStatus);
      const res = await api.get(`/agenda?${params}`);
      const expandidos = expandirPorTecnico(expandirRecorrencia(res.data, ano, mes));
      setEventos(expandidos);
      // Recarregar cores se necessário
      if (Object.keys(usuariosCores).length === 0) carregarUsuarios();
    } catch { toast && toast('Erro ao carregar eventos.', 'error'); }
    finally { setLoading(false); }
  }, [mes, ano, filtroTecnico, filtroStatus]);

  const carregarHoje = useCallback(async () => {
    try {
      // Envia a data local do navegador para evitar erro de fuso no servidor
      const agora = new Date();
      const pad = n => String(n).padStart(2,'0');
      const dataLocal = `${agora.getFullYear()}-${pad(agora.getMonth()+1)}-${pad(agora.getDate())}`;
      const res = await api.get(`/agenda/hoje?data=${dataLocal}`);
      setEventosHoje(res.data);
    } catch {}
  }, []);

  const [usuariosCores, setUsuariosCores] = useState({});

  const carregarUsuarios = useCallback(async () => {
    try {
      const res = await api.get('/usuarios');
      const ativos = res.data.filter(u => u.ativo !== false);
      setUsuarios(ativos);
      // Montar mapa id -> cor para uso nos chips da agenda
      const mapa = {};
      ativos.forEach(u => { if (u.cor) mapa[u.id] = u.cor; });
      setUsuariosCores(mapa);
      _mapaCorTecnicos = mapa; // atualiza mapa global para corDoEvento
    } catch {}
  }, []);

  useEffect(() => { carregarEventos(); }, [carregarEventos]);
  useEffect(() => { carregarHoje(); carregarUsuarios(); }, []);

  // Quando semana muda, ajusta mes/ano
  useEffect(() => {
    if (view === 'semanal') {
      setMes(semanaInicio.getMonth());
      setAno(semanaInicio.getFullYear());
    }
  }, [semanaInicio, view]);

  const navAnterior = () => {
    if (view === 'mensal') {
      if (mes === 0) { setMes(11); setAno(a=>a-1); } else setMes(m=>m-1);
    } else {
      const d = new Date(semanaInicio);
      d.setDate(d.getDate()-7);
      setSemanaInicio(d);
    }
  };
  const navProximo = () => {
    if (view === 'mensal') {
      if (mes === 11) { setMes(0); setAno(a=>a+1); } else setMes(m=>m+1);
    } else {
      const d = new Date(semanaInicio);
      d.setDate(d.getDate()+7);
      setSemanaInicio(d);
    }
  };
  const irHoje = () => {
    const n = new Date();
    setAno(n.getFullYear()); setMes(n.getMonth());
    setSemanaInicio(startOfWeek(n));
  };

  const onSave = () => {
    setModal(null);
    carregarEventos();
    carregarHoje();
    toast && toast(modal?.evento ? 'Evento atualizado!' : 'Evento criado!', 'success');
  };
  const onDelete = () => {
    setModal(null);
    carregarEventos();
    carregarHoje();
    toast && toast('Evento excluído.', 'success');
  };

  const onDayClick = (date) => {
    setModal({ type:'novo', data: date });
  };

  const onMaisClick = (date, evs) => {
    setPopoverDia({ date, eventos: evs });
  };

  const handleDrop = async (targetDate) => {
    if (!dragEvId) return;

    const isRecorrente = typeof dragEvId === 'string' && dragEvId.includes('_rec_');
    const realId = isRecorrente
      ? Number(dragEvId.split('_rec_')[0])
      : dragEvId;

    const ev = eventos.find(e => e.id === realId || e.id === dragEvId);
    if (!ev) { setDragEvId(null); return; }

    const pad = n => String(n).padStart(2, '0');

    // Calcula novas datas preservando horário/duração original
    let novaIni, novaFim;
    if (ev.dia_todo) {
      const d = new Date(targetDate);
      const dataStr = `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}`;
      const iniOrig = parseDateLocal(ev.data_inicio);
      const fimOrig = parseDateLocal(ev.data_fim);
      const durDias = Math.round((fimOrig - iniOrig) / (1000*60*60*24));
      const fimDate = new Date(d); fimDate.setDate(fimDate.getDate() + durDias);
      novaIni = dataStr;
      novaFim = `${fimDate.getFullYear()}-${pad(fimDate.getMonth()+1)}-${pad(fimDate.getDate())}`;
    } else {
      const iniOrig = new Date(ev.data_inicio);
      const durMs = new Date(ev.data_fim) - iniOrig;
      const novo = new Date(targetDate);
      novo.setHours(iniOrig.getHours(), iniOrig.getMinutes(), 0, 0);
      novaIni = novo.toISOString();
      novaFim = new Date(novo.getTime() + durMs).toISOString();
    }

    if (isRecorrente) {
      setModalDropRec({ ev, novaIni, novaFim, realId });
      setDragEvId(null);
      return;
    }

    // Evento normal — move direto
    try {
      await api.patch(`/agenda/${realId}`, {
        data_inicio: novaIni, data_fim: novaFim,
        titulo: ev.titulo || null, descricao: ev.descricao ?? null,
        endereco: ev.endereco ?? null, cor: ev.cor || null,
        status: ev.status || null, dia_todo: ev.dia_todo || false,
        tecnico_id: ev.tecnico_id || null, tecnico_nome: ev.tecnico_nome || null,
        tecnicos_ids: ev.tecnicos_ids || null, tecnicos_nomes: ev.tecnicos_nomes || null,
      });
      carregarEventos(); carregarHoje();
      toast && toast('Evento movido!', 'success');
    } catch { toast && toast('Erro ao mover evento.', 'error'); }
    setDragEvId(null);
  };

  const executarDropRecorrente = async (opcao) => {
    if (!modalDropRec) return;
    const { ev, novaIni, novaFim, realId } = modalDropRec;
    setModalDropRec(null);

    const pad = n => String(n).padStart(2, '0');
    const camposBase = {
      titulo: ev.titulo || null, descricao: ev.descricao ?? null,
      endereco: ev.endereco ?? null, cor: ev.cor || null,
      status: ev.status || null, dia_todo: ev.dia_todo || false,
      tecnico_id: ev.tecnico_id || null, tecnico_nome: ev.tecnico_nome || null,
      tecnicos_ids: ev.tecnicos_ids || null, tecnicos_nomes: ev.tecnicos_nomes || null,
    };

    // Data da ocorrência que está sendo movida
    const dataOcorrencia = ev._dataOcorrencia || (() => {
      const d = parseDateLocal(ev.data_inicio);
      return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}`;
    })();

    try {
      if (opcao === 'este') {
        // 1. Registra exceção para esta data no evento original
        await api.post(`/agenda/${realId}/excecao`, { data_excecao: dataOcorrencia });
        // 2. Cria evento independente na nova data
        await api.post('/agenda', {
          ...camposBase,
          data_inicio: novaIni, data_fim: novaFim,
          recorrencia: 'nenhuma', rec_fim: null,
        });

      } else if (opcao === 'este_e_seguintes') {
        // 1. Encerra série original um dia antes desta ocorrência
        const dAntes = parseDateLocal(dataOcorrencia);
        dAntes.setDate(dAntes.getDate() - 1);
        const recFimNovo = `${dAntes.getFullYear()}-${pad(dAntes.getMonth()+1)}-${pad(dAntes.getDate())}`;
        await api.patch(`/agenda/${realId}`, { rec_fim: recFimNovo });
        // 2. Cria nova série a partir da nova data
        await api.post('/agenda', {
          ...camposBase,
          data_inicio: novaIni, data_fim: novaFim,
          recorrencia: ev.recorrencia, rec_fim: ev.rec_fim || null,
        });

      } else if (opcao === 'todos') {
        // Calcula deslocamento em dias entre a ocorrência arrastada e o destino
        // e aplica no evento RAIZ — preserva o mês original da série
        const pad = n => String(n).padStart(2, '0');
        const dOcorrencia = parseDateLocal(dataOcorrencia);
        const dDestinoStr = typeof novaIni === 'string' && novaIni.length === 10
          ? novaIni : new Date(novaIni).toISOString().substring(0, 10);
        const dDestino = parseDateLocal(dDestinoStr);
        const deslocDias = Math.round((dDestino - dOcorrencia) / (1000 * 60 * 60 * 24));

        // Busca o evento raiz direto do backend — pode não estar no mês visível
        const { data: evRaiz } = await api.get(`/agenda/${realId}`);
        if (!evRaiz) { toast && toast('Evento original não encontrado.', 'error'); return; }

        let novaIniRaiz, novaFimRaiz;
        if (evRaiz.dia_todo) {
          const dR = parseDateLocal(evRaiz.data_inicio); dR.setDate(dR.getDate() + deslocDias);
          const dF = parseDateLocal(evRaiz.data_fim);   dF.setDate(dF.getDate() + deslocDias);
          novaIniRaiz = `${dR.getFullYear()}-${pad(dR.getMonth()+1)}-${pad(dR.getDate())}`;
          novaFimRaiz = `${dF.getFullYear()}-${pad(dF.getMonth()+1)}-${pad(dF.getDate())}`;
        } else {
          const dR = new Date(evRaiz.data_inicio); dR.setDate(dR.getDate() + deslocDias);
          const dF = new Date(evRaiz.data_fim);   dF.setDate(dF.getDate() + deslocDias);
          novaIniRaiz = dR.toISOString();
          novaFimRaiz = dF.toISOString();
        }

        await api.patch(`/agenda/${realId}`, {
          ...camposBase,
          data_inicio: novaIniRaiz,
          data_fim:    novaFimRaiz,
        });
      }

      carregarEventos(); carregarHoje();
      toast && toast('Evento movido!', 'success');
    } catch { toast && toast('Erro ao mover evento.', 'error'); }
  };
  const onSlotClick = (datetime) => {
    setModal({ type:'novo', data: datetime });
  };
  const onEventClick = (ev) => {
    // Passa a ocorrência como está (virtual ou real) — o CardDetalhe precisa de _dataOcorrencia
    setModal({ type:'detalhe', evento: ev });
  };

  const inp = { padding:'7px 10px', borderRadius:8, border:`1px solid ${C.border}`, fontSize:13, color:C.text, background:C.surface, outline:'none' };

  // Label do período atual
  const periodoLabel = view === 'mensal'
    ? `${MESES[mes]} ${ano}`
    : (() => {
        const fim = new Date(semanaInicio); fim.setDate(fim.getDate()+6);
        return `${semanaInicio.getDate()} ${MESES[semanaInicio.getMonth()].slice(0,3)} – ${fim.getDate()} ${MESES[fim.getMonth()].slice(0,3)} ${fim.getFullYear()}`;
      })();

  return (
    <div style={S.page}>
      {/* Header */}
      <div style={S.header}>
        {/* Título + badge */}
        <div style={{ display:'flex', alignItems:'center', gap:10, flex:1, minWidth:200 }}>
          <h2 style={S.title}>📅 Agenda</h2>
          {eventosHoje.length > 0 && (
            <span style={{ background:C.orange, color:'#fff', borderRadius:20, padding:'2px 10px', fontSize:12, fontWeight:700 }}>
              {eventosHoje.length} hoje
            </span>
          )}
        </div>

        {/* Filtros */}
        <select style={inp} value={filtroTecnico} onChange={e=>setFiltroTecnico(e.target.value)}>
          <option value="">Todos os técnicos</option>
          {usuarios.map(u=><option key={u.id} value={u.id}>{u.nome}</option>)}
        </select>


        {/* Navegação */}
        <button style={S.navBtn} onClick={navAnterior}>‹</button>
        <span style={S.monthLabel}>{periodoLabel}</span>
        <button style={S.navBtn} onClick={navProximo}>›</button>
        <button style={{ ...S.navBtn, fontSize:13 }} onClick={irHoje}>Hoje</button>

        {/* Toggle visão */}
        <div style={S.toggleGroup}>
          <button style={S.toggleBtn(view==='mensal')} onClick={()=>setView('mensal')}>Mês</button>
          <button style={S.toggleBtn(view==='semanal')} onClick={()=>setView('semanal')}>Semana</button>
        </div>

        {/* Novo evento */}
        <button style={S.btnPrimary} onClick={()=>setModal({ type:'novo' })}>
          <span style={{ fontSize:18, lineHeight:1 }}>+</span> Novo Evento
        </button>
      </div>

      {loading && (
        <div style={{ textAlign:'center', padding:24, color:C.muted, fontSize:14 }}>Carregando eventos...</div>
      )}

      {/* Painel hoje — sempre em cima (mobile e desktop) */}
      {view === 'mensal' && eventosHoje.length > 0 && (
        <div style={{ padding:'12px 16px 0' }}>
          <PainelHoje eventos={eventosHoje} onEventClick={onEventClick} />
        </div>
      )}

      {/* Conteúdo — calendário */}
      <div style={{ display:'flex', alignItems:'flex-start' }}>
        <div style={{ flex:1, minWidth:0 }}>
          {view === 'mensal' ? (
            <ViewMensal
              eventos={eventos}
              ano={ano} mes={mes} hoje={hoje}
              onDayClick={onDayClick}
              onEventClick={onEventClick}
              dragEvId={dragEvId}
              setDragEvId={setDragEvId}
              onDrop={handleDrop}
              onMaisClick={onMaisClick}
              usuariosCores={usuariosCores}
            />
          ) : (
            <ViewSemanal
              eventos={eventos}
              semanaInicio={semanaInicio}
              hoje={hoje}
              onSlotClick={onSlotClick}
              onEventClick={onEventClick}
            />
          )}
        </div>
      </div>



      {/* Popover "+N mais" — todos os eventos do dia */}
      {popoverDia && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.35)', zIndex:998, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}
          onClick={() => setPopoverDia(null)}>
          <div style={{ background:C.surface, borderRadius:14, width:'100%', maxWidth:420, maxHeight:'80vh', overflowY:'auto', boxShadow:'0 16px 48px rgba(0,0,0,.22)' }}
            onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div style={{ background:C.navy, borderRadius:'14px 14px 0 0', padding:'14px 20px', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
              <span style={{ color:'#fff', fontWeight:700, fontSize:15 }}>
                📅 {new Date(popoverDia.date).toLocaleDateString('pt-BR', { weekday:'long', day:'numeric', month:'long' })}
              </span>
              <button onClick={() => setPopoverDia(null)} style={{ background:'none', border:'none', color:'#fff', fontSize:20, cursor:'pointer' }}>×</button>
            </div>
            <div style={{ padding:16, display:'flex', flexDirection:'column', gap:8 }}>
              {popoverDia.eventos.map(ev => (
                <div key={ev.id}
                  onClick={() => { setPopoverDia(null); onEventClick(ev); }}
                  style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 12px', borderRadius:10, border:`1px solid ${C.border}`, cursor:'pointer', background:C.surface, transition:'background .12s' }}
                  onMouseEnter={e => e.currentTarget.style.background=C.bg}
                  onMouseLeave={e => e.currentTarget.style.background=C.surface}>
                  <div style={{ width:10, height:10, borderRadius:'50%', background:corDoEvento(ev, usuariosCores), flexShrink:0 }} />
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontSize:13, fontWeight:600, color:C.navy, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{ev.titulo}</div>
                    <div style={{ fontSize:11, color:C.muted, marginTop:2 }}>
                      {ev.dia_todo ? 'Dia todo' : `${fmtHora(ev.data_inicio)} – ${fmtHora(ev.data_fim)}`}
                      {ev.tecnico_nome ? ` · 👤 ${ev.tecnico_nome}` : ''}
                    </div>
                  </div>

                </div>
              ))}
              <button
                onClick={() => { setPopoverDia(null); onDayClick(popoverDia.date); }}
                style={{ background:C.orange, color:'#fff', border:'none', borderRadius:8, padding:'9px', fontSize:13, fontWeight:600, cursor:'pointer', marginTop:4 }}>
                + Novo evento neste dia
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de opções para arrastar evento recorrente */}
      {modalDropRec && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:2000, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}>
          <div style={{ background:'#fff', borderRadius:16, width:'100%', maxWidth:380, boxShadow:'0 20px 60px rgba(0,0,0,0.3)', overflow:'hidden' }}>
            <div style={{ background:C.navy, padding:'18px 24px' }}>
              <div style={{ color:'#fff', fontWeight:700, fontSize:16 }}>⟳ Editar evento recorrente</div>
              <div style={{ color:'rgba(255,255,255,0.7)', fontSize:13, marginTop:4 }}>
                "{modalDropRec.ev.titulo}"
              </div>
            </div>
            <div style={{ padding:20, display:'flex', flexDirection:'column', gap:10 }}>
              <p style={{ fontSize:13, color:C.muted, margin:'0 0 4px' }}>
                Você quer alterar:
              </p>
              {[
                { opcao: 'este',             icone: '📌', label: 'Apenas este evento',              desc: 'Só esta ocorrência será movida' },
                { opcao: 'este_e_seguintes', icone: '📅', label: 'Este e os eventos seguintes',     desc: 'Esta e todas as próximas ocorrências' },
                { opcao: 'todos',            icone: '🔄', label: 'Todos os eventos da série',       desc: 'Todas as ocorrências serão movidas' },
              ].map(({ opcao, icone, label, desc }) => (
                <button key={opcao} onClick={() => executarDropRecorrente(opcao)}
                  style={{
                    display:'flex', alignItems:'center', gap:14,
                    padding:'12px 16px', border:`1.5px solid ${C.border}`,
                    borderRadius:10, background:'#f9fafb', cursor:'pointer',
                    textAlign:'left', transition:'all .15s',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = C.orange; e.currentTarget.style.background = 'rgba(216,90,48,0.05)'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.background = '#f9fafb'; }}
                >
                  <span style={{ fontSize:22 }}>{icone}</span>
                  <div>
                    <div style={{ fontWeight:700, fontSize:14, color:C.navy }}>{label}</div>
                    <div style={{ fontSize:12, color:C.muted, marginTop:2 }}>{desc}</div>
                  </div>
                </button>
              ))}
              <button onClick={() => setModalDropRec(null)}
                style={{ marginTop:4, padding:'9px', border:`1px solid ${C.border}`, borderRadius:8, background:'transparent', color:C.muted, cursor:'pointer', fontSize:13 }}>
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modais */}
      {modal?.type === 'novo' && (
        <ModalEvento
          evento={modal.data ? {
            data_inicio: (() => { const d=new Date(modal.data); const p=n=>String(n).padStart(2,'0'); return `${d.getFullYear()}-${p(d.getMonth()+1)}-${p(d.getDate())}T${p(d.getHours()||9)}:00`; })(),
            data_fim:    (() => { const d=new Date(modal.data); const p=n=>String(n).padStart(2,'0'); return `${d.getFullYear()}-${p(d.getMonth()+1)}-${p(d.getDate())}T${p((d.getHours()||9)+1)}:00`; })(),
          } : null}
          usuarios={usuarios}
          onSave={onSave}
          onDelete={onDelete}
          onClose={()=>setModal(null)}
        />
      )}
      {modal?.type === 'editar' && (
        <ModalEvento
          evento={modal.evento}
          usuarios={usuarios}
          onSave={onSave}
          onDelete={onDelete}
          onClose={()=>setModal(null)}
        />
      )}
      {modal?.type === 'detalhe' && (
        <CardDetalhe
          evento={modal.evento}
          onEdit={() => {
            const ev = modal.evento;
            if (ev._recorrente) {
              setModal({ type: 'opcoes_recorrencia', evento: ev });
            } else {
              setModal({ type: 'editar', evento: ev });
            }
          }}
          onClose={()=>setModal(null)}
        />
      )}
      {modal?.type === 'opcoes_recorrencia' && modal.evento && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:2000, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}>
          <div style={{ background:'#fff', borderRadius:16, width:'100%', maxWidth:380, boxShadow:'0 20px 60px rgba(0,0,0,0.3)', overflow:'hidden' }}>
            <div style={{ background:C.navy, padding:'18px 24px' }}>
              <div style={{ color:'#fff', fontWeight:700, fontSize:16 }}>⟳ Editar evento recorrente</div>
              <div style={{ color:'rgba(255,255,255,0.7)', fontSize:13, marginTop:4 }}>"{modal.evento.titulo}"</div>
            </div>
            <div style={{ padding:20, display:'flex', flexDirection:'column', gap:10 }}>
              <p style={{ fontSize:13, color:C.muted, margin:'0 0 4px' }}>Você quer alterar:</p>
              {[
                { tipo:'este',              icone:'📌', label:'Apenas este evento',         desc:'Só esta ocorrência será editada' },
                { tipo:'este_e_seguintes',  icone:'📅', label:'Este e os seguintes',        desc:'Esta e todas as próximas ocorrências' },
                { tipo:'todos',             icone:'🔄', label:'Todos os eventos da série',  desc:'Todas as ocorrências serão alteradas' },
              ].map(({ tipo, icone, label, desc }) => (
                <button key={tipo} onClick={() => {
                  const ev = modal.evento;
                  const pad = n => String(n).padStart(2,'0');
                  const dataOcorrencia = ev._dataOcorrencia || (() => {
                    const d = parseDateLocal(ev.data_inicio);
                    return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}`;
                  })();
                  const realId = ev._idOriginal || ev.id;

                  if (tipo === 'este') {
                    // Abre modal de edição com _criarComoNovo e dados da ocorrência
                    setModal({ type:'editar', evento:{
                      ...ev,
                      id: null,
                      _criarComoNovo: true,
                      _idOriginalExcecao: realId,  // para registrar exceção ao salvar
                      _dataExcecao: dataOcorrencia,
                      recorrencia: 'nenhuma',
                      rec_fim: null,
                    }});
                  } else if (tipo === 'este_e_seguintes') {
                    // Encerra série antes desta data e abre edição com nova série
                    const dAntes = parseDateLocal(dataOcorrencia);
                    dAntes.setDate(dAntes.getDate() - 1);
                    const recFimNovo = `${dAntes.getFullYear()}-${pad(dAntes.getMonth()+1)}-${pad(dAntes.getDate())}`;
                    api.patch(`/agenda/${realId}`, { rec_fim: recFimNovo }).then(() => {
                      setModal({ type:'editar', evento:{
                        ...ev,
                        id: null,
                        _criarComoNovo: true,
                        recorrencia: ev.recorrencia,
                        rec_fim: ev.rec_fim || null,
                      }});
                    });
                  } else {
                    const orig = eventos.find(e => e.id === realId) || ev;
                    setModal({ type:'editar', evento:{ ...orig, id: realId }});
                  }
                }}
                  style={{ display:'flex', alignItems:'center', gap:14, padding:'12px 16px', border:`1.5px solid ${C.border}`, borderRadius:10, background:'#f9fafb', cursor:'pointer', textAlign:'left', transition:'all .15s' }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor=C.orange; e.currentTarget.style.background='rgba(216,90,48,0.05)'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor=C.border; e.currentTarget.style.background='#f9fafb'; }}
                >
                  <span style={{ fontSize:22 }}>{icone}</span>
                  <div>
                    <div style={{ fontWeight:700, fontSize:14, color:C.navy }}>{label}</div>
                    <div style={{ fontSize:12, color:C.muted, marginTop:2 }}>{desc}</div>
                  </div>
                </button>
              ))}
              <button onClick={() => setModal(null)} style={{ marginTop:4, padding:'9px', border:`1px solid ${C.border}`, borderRadius:8, background:'transparent', color:C.muted, cursor:'pointer', fontSize:13 }}>
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
